import React, { useState } from 'react'
import { Button, Upload, message, Alert } from 'antd'
import { Plus, FileText, AlertCircle, ArrowRight, Zap, Info } from 'lucide-react'
import { useFFEStore } from '../../store/ffeStore'
import { useApi } from '../../services/ApiContext'
import { useCompanyStore } from '../../store/company/companyStore'
import { Link as RouterLink } from 'react-router-dom'
import type { UploadProps, UploadFile } from 'antd'

interface Props {
  groupId?: string
  isPrimary?: boolean
}

export const AddFromQuoteButton: React.FC<Props> = ({ groupId, isPrimary = false }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const plan = useCompanyStore((state) => state.company.plan)
  const user = useCompanyStore((state) => state.user)
  
  const addLineItem = useFFEStore((state) => state.addLineItem)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const removeLineItem = useFFEStore((state) => state.removeLineItem)
  const setLineItemLoading = useFFEStore((state) => state.setLineItemLoading)
  const suppliers = useFFEStore((state) => state.suppliers)
  const addSupplier = useFFEStore((state) => state.addSupplier)
  const defaultMargin = useFFEStore(
    (state) => state.document.settings.defaultMargin
  )
  
  const api = useApi()

  // Check if user is on free plan
  const isFreePlan = !plan || plan === 'free'

  const upgradeButtonRedirect = (user?: any) => {
    return user?.isDemo ? '/signup?redirect=/app/upgrade' : '/app/upgrade'
  }

  const isValidPdfFile = (file: File): boolean => {
    return file.type === 'application/pdf' && file.size <= 20 * 1024 * 1024 // 20MB limit
  }

  const handleFileUpload = async (file: File) => {
    if (!isValidPdfFile(file)) {
      setError('Please upload a valid PDF file (max 20MB)')
      return
    }

    setError(null)
    setUploading(true)
    setProcessing(false)

    let loadingLineItemId: string | null = null

    try {
      // Add placeholder line item immediately and capture its id
      addLineItem(
        {
          name: 'Processing PDF quote…',
          description: '',
          quantity: 1,
          margin: defaultMargin,
          costPrice: 0,
          sellPrice: 0,
          status: 'specified',
          groupId
        },
        undefined,
        undefined,
        (id) => {
          loadingLineItemId = id
          setLineItemLoading(id, true)
        }
      )

      // Close popover immediately for a snappier UX
      setIsPopoverOpen(false)
      setFileList([])

      // Step 1: Get presigned upload URL
      const presignedResponse = await api.getPresignedUpload({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        folder: 'quotes'
      })

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(presignedResponse.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3')
      }

      setProcessing(true)

      // Step 3: Process PDF with add-items-by-quote service
      const result = await api.addItemsByQuote({
        s3Key: presignedResponse.s3Key
      })

      // Step 4: Create line items from extracted data
      if (result.lineItems && result.lineItems.length > 0) {
        // Remove the loading line item
        if (loadingLineItemId) {
          removeLineItem(loadingLineItemId)
        }

        // Create all the actual line items
        let activeSuppliers = suppliers.filter((s) => !s.isArchived)
        for (const item of result.lineItems) {
          // Match or create supplier by name
          let supplierIdToSet: string | undefined
          const supplierName = (item?.supplier || '').trim()
          if (supplierName) {
            const existing = activeSuppliers.find(
              (s) => s.name.toLowerCase() === supplierName.toLowerCase()
            )
            if (existing) {
              supplierIdToSet = existing._id
            } else {
              const newSupplier = { name: supplierName }
              const response = await api.createSupplier(newSupplier)
              activeSuppliers.push(response)
              addSupplier(response)
              supplierIdToSet = response._id
            }
          }

          // Create line item
          addLineItem(
            {
              name: item.name || 'Unnamed Product',
              description: item.description || '',
              internalNotes: item.internalNotes || '',
              brand: item.brand || '',
              quantity: item.quantity || 1,
              costPrice: item.costPrice || 0,
              dimensions: item.dimensions,
              supplier: supplierIdToSet,
              supplierItemCode: item.supplierItemCode || '',
              productUrl: item.productUrl || '',
              image: item.image,
              additionalImages: item.additionalImages || [],
              leadTime: item.leadTime || '',
              margin: defaultMargin,
              sellPrice: 0,
              status: 'specified',
              groupId
            }
          )
        }

        message.success(`Successfully added ${result.lineItems.length} products from quote!`)
      } else {
        // Update the loading line item to show no products found
        if (loadingLineItemId) {
          updateLineItem(loadingLineItemId, {
            name: 'No products found in PDF',
            description: 'The PDF could not be processed or contained no product information.',
            status: 'specified'
          })
          setLineItemLoading(loadingLineItemId, false)
        }
        message.warning('No products could be extracted from the PDF')
      }

      // Reset states
      setProcessing(false)

    } catch (err) {
      console.error('Error processing PDF quote:', err)
      
      // Remove the loading line item if processing fails
      if (loadingLineItemId) {
        removeLineItem(loadingLineItemId)
      }
      
      setError('Failed to process PDF quote. Please check the file and try again.')
      setProcessing(false)
    } finally {
      setUploading(false)
    }
  }

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    const { fileList: newFileList } = info
    setFileList(newFileList)
    
    // Auto-upload when file is selected
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      handleFileUpload(newFileList[0].originFileObj as File)
    }
  }

  const beforeUpload = (file: File) => {
    if (!isValidPdfFile(file)) {
      setError('Please upload a valid PDF file (max 20MB)')
      return false
    }
    setError(null)
    return false // Prevent auto upload, we'll handle it manually
  }

  // Upgrade prompt content for free plan users
  const upgradeContent = (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-5 w-5 text-primary" />
          <h4 className="text-sm font-medium text-gray-900">
            Add Products from Quote
          </h4>
        </div>
        <p className="text-xs text-gray-600 mb-4">
          Upload a PDF quote to automatically extract product details including names, descriptions, images, pricing, and specifications. Save hours of manual data entry.
        </p>
      </div>
      
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            <span>AI-powered PDF text extraction</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            <span>Automatic product data parsing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            <span>Bulk import multiple products at once</span>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2">
        <RouterLink to={upgradeButtonRedirect(user)} className="flex-1">
          <Button type="primary" className="w-full">
            Upgrade to Unlock
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </RouterLink>
        <Button
          onClick={() => setIsPopoverOpen(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  )

  // Regular content for paid plan users
  const regularContent = (
    <div className="p-4">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Add Products from Quote
        </h4>
        <p className="text-xs text-gray-600 mb-4">
          Upload a PDF quote to automatically extract product details including names, descriptions, images, pricing, and specifications.
        </p>
      </div>
      
      {/* Banner with instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-800">
            <p className="font-medium mb-1">Tip:</p>
            <ul className="space-y-1 text-blue-700">
              <li>• Hover over product images to select the right photo from related images</li>
              <li>• Review product details for accuracy</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        <Upload.Dragger
          accept=".pdf"
          beforeUpload={beforeUpload}
          onChange={handleUploadChange}
          fileList={fileList}
          showUploadList={false}
          disabled={uploading || processing}
        >
          <p className="ant-upload-drag-icon">
            <FileText className="h-8 w-8 text-gray-400" />
          </p>
          <p className="ant-upload-text">
            {uploading || processing ? 'Processing...' : 'Click or drag PDF to upload'}
          </p>
          <p className="ant-upload-hint">
            PDF files only, max 20MB
          </p>
        </Upload.Dragger>
        
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            icon={<AlertCircle className="h-3 w-3" />}
            className="text-xs"
          />
        )}
        
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setIsPopoverOpen(false)
              setFileList([])
              setError(null)
              setUploading(false)
              setProcessing(false)
            }}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="inline-block">
      <Button
        type={isPrimary ? 'primary' : 'default'}
        icon={<Plus className="h-4 w-4" />}
        onClick={() => setIsPopoverOpen(true)}
      >
        Add from Quote
      </Button>
      
      {isPopoverOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* {isFreePlan ? upgradeContent : regularContent} */}
            {regularContent}
          </div>
        </div>
      )}
    </div>
  )
}
